import {css} from '@emotion/react'
import {useStaticQuery, graphql} from 'gatsby'

import Logo from '../../../images/startpage/logo.svg'
import SpLogo from '../../../images/startpage/sp-logo-3.svg'
import ShieldIcon from '../../../images/startpage/icons/shield-ON.svg'
import IPhoneIcon from '../../../images/startpage/icons/iphone.svg'
import AndroidIcon from '../../../images/startpage/icons/android.svg'
import QRCodeMarketingPage from './campaigns/QRCodeMarketingPage.svg'

const translations = {
    en: {
        title: 'Private AI Chat',
        features: [
            {
                icon: ShieldIcon,
                text: 'Your chats are never tracked',
            },
            {
                icon: ShieldIcon,
                text: 'Chats are stored on your device, not the cloud',
            },
            {
                icon: ShieldIcon,
                text: 'Clear chats with a single tap',
            },
        ],
        // Download section translations
        download: 'Download',
        downloadFor: 'for iPhone or Android',
        scanQrCode: 'Scan QR code with your phone to get the app.',
        // Footer translations
        termsOfService: 'Terms of Service',
        privacyPolicy: 'Privacy Policy',
        aboutUs: 'About Us',
    },
    de: {
        title: 'Privater KI-Chat',
        features: [
            {
                icon: ShieldIcon,
                text: 'Ihre Chats werden niemals verfolgt',
            },
            {
                icon: ShieldIcon,
                text: 'Chats werden auf Ihrem Gerät gespeichert, nicht in der Cloud',
            },
            {
                icon: ShieldIcon,
                text: 'Chats mit einem einzigen Fingertipp löschen',
            },
        ],
        // Download section translations
        download: 'Herunterladen',
        downloadFor: 'für iPhone oder Android',
        scanQrCode: 'QR-Code mit Ihrem Telefon scannen, um die App zu erhalten.',
        // Footer translations
        termsOfService: 'Nutzungsbedingungen',
        privacyPolicy: 'Datenschutzrichtlinie',
        aboutUs: 'Über uns',
    },
    nl: {
        title: 'Privé AI Chat',
        features: [
            {
                icon: ShieldIcon,
                text: 'Uw chats worden nooit gevolgd',
            },
            {
                icon: ShieldIcon,
                text: 'Chats worden opgeslagen op uw apparaat, niet in de cloud',
            },
            {
                icon: ShieldIcon,
                text: 'Chats wissen met één tik',
            },
        ],
        // Download section translations
        download: 'Downloaden',
        downloadFor: 'voor iPhone of Android',
        scanQrCode: 'Scan QR-code met uw telefoon om de app te downloaden.',
        // Footer translations
        termsOfService: 'Servicevoorwaarden',
        privacyPolicy: 'Privacybeleid',
        aboutUs: 'Over ons',
    },
}

const PrivateAIUnsupported = ({languageCode = 'en', experimentId, os, browser}) => {
    const text = translations[languageCode] || translations.en

    const images = useStaticQuery(graphql`
        query PrivateAIUnsupportedImages {
            desktopPhone: file(relativePath: {eq: "startpage/phones/app-chat.png"}) {
                publicURL
            }
        }
    `)

    return (
        <section
            className="privateai-unsupported"
            css={css`
                width: 100%;
                min-height: 100vh;
                background: linear-gradient(133deg, #000 15.99%, #5668F1 90.57%);
                background-attachment: fixed;
                position: relative;
                overflow: hidden;
                display: flex;
                flex-direction: column;

                @media (max-width: 640px) {
                    min-height: 100vh;
                    height: auto;
                    overflow: visible;
                }
            `}
        >
            {/* Main Content */}
            <div
                className="main-content"
                css={css`
                    flex: 1;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    position: relative;
                    z-index: 2;
                    padding: 30px 20px 20px;

                    @media (max-width: 1200px) {
                        padding: 20px 15px 15px;
                    }

                    @media (max-width: 900px) {
                        padding: 40px 20px 20px;
                    }
                `}
            >
                <div
                    className="content-wrapper"
                    css={css`
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        max-width: 1000px;
                        width: 100%;
                        gap: 45px;
                        margin: 0 15px;
                        @media (max-width: 942px) {
                            flex-direction: column;
                        }
                        @media (max-width: 640px) {
                            
                            text-align: left;
                            gap: 90px;
                            max-width: 500px;
                            margin: 0 auto;
                            align-items: flex-start;
                        }
                        
                    `}
                >
                    {/* Left Side - Content */}
                    <div
                        className="content"
                        css={css`
                            flex: 1;

                            @media (max-width: 640px) {
                                max-width: 100%;
                                text-align: left;
                                padding: 0 20px;
                                margin: auto;
                            }
                        `}
                    >
                        {/* Logo */}
                        <div
                            className="logo"
                            css={css`
                                display: flex;
                                align-items: center;
                                gap: 10px;
                                margin-bottom: 32px;
                            `}
                        >
                            <SpLogo
                                css={css`
                                    width: 52px;
                                    height: 52.02px;
                                `}
                            />
                        </div>
                        <Logo />
                        <h1
                            css={css`
                                color: #38DDCC;
                                font-family: Inter;
                                font-size: 72px;
                                font-weight: 700;
                                line-height: normal;
                                letter-spacing: 0.432px;
                                margin: 0 0 32px 0;
                                @media (max-width: 1000px) {
                                    font-size: 60px;
                                }
                                @media (max-width: 875px) {
                                    font-size: 50px;
                                }
                                @media (max-width: 768px) {
                                    font-size: 45px;
                                }
                                
                            `}
                        >
                            {text.title}
                        </h1>

                        {/* Features List */}
                        <ul
                            css={css`
                                list-style: none;
                                padding: 0;
                                margin: 0 0 32px 0;

                                @media (max-width: 640px) {
                                    text-align: left;
                                    max-width: 400px;
                                    margin-bottom: 40px;
                                }
                            `}
                        >
                            {text.features.map((feature, index) => (
                                <li
                                    key={index}
                                    css={css`
                                        display: flex;
                                        align-items: center;
                                        gap: 12px;
                                        margin-bottom: 16px;
                                        line-height: 1.5;
                                    `}
                                >
                                    <feature.icon
                                        css={css`
                                            width: 28px;
                                            height: 28px;
                                            flex-shrink: 0;
                                        `}
                                    />
                                    <span
                                        css={css`
                                            color: #f2f3fb;
                                            font-family: 'Inter';
                                            font-size: 18px;
                                            font-weight: 400;
                                            line-height: 21px;
                                            letter-spacing: 0.3px;
                                            @media (max-width: 900px) {
                                                font-size: 15px;
                                            }
                                        `}
                                    >
                                        {feature.text}
                                    </span>
                                </li>
                            ))}
                        </ul>

                        {/* Download Section */}
                        <a
                            href="https://startpage.go.link/40TNI"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="download-section"
                            css={css`
                                display: flex;
                                align-items: center;
                                gap: 20px;
                                padding: 20px;
                                border-radius: 30px;
                                border: 2px solid #38DDCC;
                                background-clip: padding-box;
                                position: relative;
                                justify-content: center;
                                text-decoration: none;
                                cursor: pointer;
                                transition: all 0.2s ease;
                              
                                max-width: 100%;
                                min-height: 136px;
                                height: auto;
                                box-sizing: border-box;

                                &:hover {
                                    border-color: #00D4FF;
                                    transform: translateY(-2px);
                                }

                                &::before {
                                    content: '';
                                    position: absolute;
                                    top: 0;
                                    left: 0;
                                    right: 0;
                                    bottom: 0;
                                   
                                    border-radius: 16px;
                                    z-index: -1;
                                }

                                @media (max-width: 768px) {
                                    width: 100%;
                                    padding: 16px;
                                    gap: 16px;
                                }

                                @media (max-width: 640px) {
                                    flex-direction: column;
                                    text-align: center;
                                    gap: 15px;
                                    padding: 20px;
                                    max-width: 270px;
                                    margin: auto;
                                }
                            `}
                        >
                            {/* Download Text */}
                            <div
                                css={css`
                                    display: flex;
                                    flex-direction: column;
                                    align-items: flex-start;
                                    gap: 4px;
                                    flex: 1;
                                    min-width: 0;
                                    max-width: 250px;
                                    order: 1;

                                    @media (max-width: 640px) {
                                        align-items: center;
                                        width: 100%;
                                        max-width: none;
                                    }
                                `}
                            >
                                <div
                                    css={css`
                                        display: flex;
                                        align-items: center;
                                        gap: 8px;
                                        flex-wrap: wrap;

                                        @media (max-width: 768px) {
                                            gap: 6px;
                                        }
                                    `}
                                >
                                    <span
                                        css={css`
                                            color: #EBECF7;
                                            font-family: 'Inter';
                                            font-size: 28px;
                                            font-weight: 700;
                                            line-height: 30px;

                                            @media (max-width: 1050px) {
                                                font-size: 20px;
                                                line-height: 28px;
                                            }

                                            @media (max-width: 480px) {
                                                font-size: 24px;
                                            }
                                        `}
                                    >
                                        {text.download}
                                    </span>
                                    <span
                                        css={css`
                                            color: #00D4FF;
                                            font-size: 20px;
                                            font-weight: bold;

                                            @media (max-width: 768px) {
                                                font-size: 18px;
                                            }
                                        `}
                                    >
                                        →
                                    </span>
                                </div>
                                <span
                                    css={css`
                                        color: #EBECF7;
                                        font-family: 'Inter';
                                        font-size: 16px;
                                        line-height: 30px;
                                        letter-spacing: 0.18px;
                                        display: flex;
                                        align-items: center;
                                        gap: 8px;

                                        @media (max-width: 1050px) {
                                            font-size: 12px;
                                            line-height: 24px;
                                        }
                                    `}
                                >
                                    {text.downloadFor}
                                    <IPhoneIcon
                                        css={css`
                                            width: 20px;
                                            height: 20px;
                                            flex-shrink: 0;
                                        `}
                                    />
                                    <AndroidIcon
                                        css={css`
                                            width: 20px;
                                            height: 20px;
                                            flex-shrink: 0;
                                        `}
                                    />
                                </span>
                            </div>

                            {/* QR Code - Centered */}
                            <a
                                href="https://startpage.go.link/40TNI"
                                target="_blank"
                                rel="noopener noreferrer"
                                css={css`
                                    max-width: 120px;
                                    background: #FFFFFF;
                                    border-radius: 8px;
                                    display: flex;
                                    align-items: center;
                                    justify-content: center;
                                    flex-shrink: 0;
                                    padding: 12px;
                                    box-sizing: border-box;
                                    order: 2;
                                    margin: 0 auto;
                                    
                                    &:hover {
                                        opacity: 0.9;
                                    }
                                    @media (max-width: 1020px) {
                                        max-width: 101px;
                                    }   
                                    @media (max-width: 950px) {
                                        max-width: 95px;
                                    }
                                    @media (max-width: 640px) {
                                        order: 1;
                                    }
                                `}
                            >
                                <img 
                                    src={QRCodeMarketingPage} 
                                    alt="QR Code - Scan to download app"
                                    css={css`
                                        width: 100%;
                                        height: 100%;
                                        object-fit: contain;
                                    `}
                                />
                            </a>

                            {/* QR Code Instructions */}
                            <div
                                css={css`
                                    background: rgba(0, 0, 0, 0.2);
                                    padding: 18px;
                                    border-radius: 30px;
                                    max-width: 175px;
                                    text-align: center;
                                    flex: 1;
                                    order: 3;
                                    margin: 0 auto;
                                    @media (max-width: 1007px) {
                                        max-width: 145px;
                                    }
                                    @media (max-width: 973px) {
                                        max-width: 135px;
                                    }
                                    @media (max-width: 640px) {
                                        order: 2;
                                       
                                        width: 100%;
                                    }
                                `}
                            >
                                <span
                                    css={css`
                                        color: #38DDCC;
                                        font-family: 'Inter';
                                        font-size: 14px;
                                        font-weight: 400;
                                        line-height: 21px;
                                        @media (max-width: 1050px) {
                                            font-size: 12px;
                                        }
                                    `}
                                >
                                    {text.scanQrCode}
                                </span>
                            </div>
                        </a>
                    </div>

                    {/* Right Side - Phone Mockup */}

                    <div
                        className="phone-mockup"
                        css={css`
                            flex: 0 0 auto;
                            width: 320px;
                            display: flex;
                            justify-content: center;
                            align-items: center;
                            position: relative;

                            @media (max-width: 640px) {
                                width: 100%;
                                max-width: 250px;
                                margin: 0 auto;
                            }

                            @media (max-width: 900px) {
                                width: 100%;
                                max-width: 280px;
                            }
                        `}
                    >
                        <div
                            className="phone-mockup-wrapper"
                            css={css`
                                position: absolute;
                                top: -35px;
                                right: -250px;
                                bottom: -12px;
                                left: -217px;
                                border-radius: 776px;
                                background: rgba(85, 11, 200, 0.8);
                                filter: blur(178.10621643066406px);
                                z-index: -1;
                            `}
                        />

                        <img
                            src={images.desktopPhone.publicURL}
                            alt="A smartphone screen of the Private AI chat app"
                            loading="eager"
                            css={css`
                                position: relative;
                                z-index: 10;
                                width: 100%;
                                height: auto;
                                max-width: 302px;
                                filter: drop-shadow(0 15px 40px rgba(0, 0, 0, 0.4));
                            `}
                        />
                    </div>
                </div>
            </div>

            {/* Footer */}
            <footer
                className="desktop-footer"
                css={css`
                    position: relative;
                    z-index: 2;
                    padding: 30px 40px;
                    display: flex;
                    justify-content: center;
                    flex-shrink: 0;
                `}
            >
                <div
                    className="footer-links"
                    css={css`
                        display: flex;
                        gap: 40px;

                        @media (max-width: 768px) {
                            gap: 24px;
                            flex-wrap: wrap;
                            justify-content: center;
                        }
                    `}
                >
                    <a
                        href="/en/terms-privateai"
                        target="_blank"
                        rel="noopener noreferrer"
                        css={css`
                            color: #EBECF7;
                            text-decoration: none;
                            font-family: 'Inter';
                            font-size: 16px;
                            font-weight: 400;
                            line-height: 16px;
                            letter-spacing: 0.3px;
                            transition: color 0.3s ease;

                            &:hover {
                                color: #00ffee;
                            }
                        `}
                    >
                        {text.termsOfService}
                    </a>
                    <a
                        href="/en/privacy-policy-privateai"
                        target="_blank"
                        rel="noopener noreferrer"
                        css={css`
                            color: #EBECF7;
                            text-decoration: none;
                            font-family: 'Inter';
                            font-size: 16px;
                            font-weight: 400;
                            line-height: 16px;
                            letter-spacing: 0.3px;
                            transition: color 0.3s ease;

                            &:hover {
                                color: #00ffee;
                            }
                        `}
                    >
                        {text.privacyPolicy}
                    </a>
                    <a
                        href="https://www.startpage.com/en/about-us"
                        target="_blank"
                        rel="noopener noreferrer"
                        css={css`
                            color: #EBECF7;
                            text-decoration: none;
                            font-family: 'Inter';
                            font-size: 16px;
                            font-weight: 400;
                            line-height: 16px;
                            letter-spacing: 0.3px;
                            transition: color 0.3s ease;

                            &:hover {
                                color: #00ffee;
                            }
                        `}
                    >
                        {text.aboutUs}
                    </a>
                </div>
            </footer>
        </section>
    )
}

export default PrivateAIUnsupported
